import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, ChevronRight } from 'lucide-react';
import { formatTierLabel } from '../utils/poketraceService';
import { headlinePrice, DEFAULT_PREFS } from '../utils/priceMath';
import { estimateMissing, describeEstimate } from '../utils/conditionEstimate';
import { ebaySoldUrl, tcgplayerUrl } from '../utils/cardLinks';
import { palette, COPY, money2 } from './pricing/pricingTokens';
import haptics from '../utils/haptics';

/**
 * The pricing block on card detail.
 *
 * Every price here is two things at once, and the design keeps them visually
 * distinct so nobody has to guess which tap does what:
 *
 *   an OUTBOUND search - the external-link icon, in link blue, leaves the app
 *   an INBOUND deep dive - the bordered chip or tile body, with a chevron
 *
 * Graded is a list of grades, because a slab has one market. Raw is a group per
 * quality, because a raw card has two - what it sold for on eBay and what
 * TCGplayer is asking - and those are different statistics that should never be
 * averaged together or shown as one number.
 *
 * The selected row is reported upward so the price history chart above the
 * heading can follow it. The chart and this list are then two views of one
 * selection and cannot disagree about which tier is being read.
 */

const RAW_ORDER = ['NEAR_MINT', 'LIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];
const RAW_SHORT = {
  NEAR_MINT: 'NM',
  LIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};
const RAW_WORD = {
  NEAR_MINT: 'Near mint',
  LIGHTLY_PLAYED: 'Lightly played',
  MODERATELY_PLAYED: 'Moderately played',
  HEAVILY_PLAYED: 'Heavily played',
  DAMAGED: 'Damaged',
};
const SHORT_KEY = { NM: 'nm', LP: 'lp', MP: 'mp', HP: 'hp', DMG: 'dmg' };
/** Short keys back to the labels this screen uses, for the estimate note. */
const RAW_SHORT_LABELS = { nm: 'NM', lp: 'LP', mp: 'MP', hp: 'HP', dmg: 'DMG' };

/** Shown before the "More" chip. The rest are real but rare. */
const PRIMARY_GRADERS = ['PSA', 'BGS', 'CGC', 'SGC'];

const price = (t) => (t?.median30d ?? t?.avg ?? null);

export default function PriceLadder({
  tiers, sources, grade, isGraded, theme, card, evidence, evidencePending, heldOut,
  isDark = true, onSelectTier, onNeedRawSources, onOpenBreakdown,
}) {
  // NA means we asked and there is no price. Until the second source lands we
  // have not asked yet, and saying NA then would be a lie the user acts on.
  const asksPending = !sources;
  // Same rule for the sold column: the vendor's aggregate arrives before the
  // individual sales, and it is a different statistic, so painting it first
  // means the number visibly corrects itself a second later.
  const soldPending = Boolean(evidencePending);

  const c = palette(theme, isDark);
  const s = styles(c);
  const [tab, setTab] = useState(isGraded ? 'graded' : 'raw');
  const [moreGraders, setMoreGraders] = useState(false);

  const askByCondition = sources?.justTCG?.conditions || null;

  const rawRows = useMemo(() => {
    const real = RAW_ORDER.map((tier) => {
      const short = RAW_SHORT[tier];
      // Individual sales when we hold them, the vendor's aggregate when we do
      // not. The row records which, because the deep dive can only be opened on
      // a number it is able to explain.
      const own = headlinePrice(evidence, tier, heldOut);
      // No fallback to the vendor's aggregate.
      //
      // It used to read `own.value ?? price(data)`, and that `??` is how a
      // Fossil Dragonite showed a PSA 10 at $13,600 with not one PSA 10 sale
      // behind it. The vendor's figure and the sales we hold are different
      // statistics from different fetches, and where they disagree the sales
      // are the ones that can be shown to somebody. A tier we cannot evidence
      // falls through to the estimator below, which says what it was worked
      // out from, or shows nothing at all.
      return {
        key: tier,
        label: short,
        word: RAW_WORD[tier],
        sold: own.value,
        fromSales: own.value != null,
        // The count has to describe the price beside it. Showing the vendor's
        // 151 next to a number derived from 25 sales overstates both.
        soldCount: own.value != null ? own.counted : null,
        thin: own.value != null && own.counted < DEFAULT_PREFS.count,
        ask: askByCondition?.[SHORT_KEY[short]]?.price ?? null,
        mine: !isGraded && (grade || 'NM').toUpperCase() === short,
      };
    });

    // Fill the blanks. Moderately played is missing on more than half the cards
    // that price at all, heavily played on two thirds, and a blank is useless to
    // somebody holding exactly that card. An estimate is drawn differently, says
    // what it was worked out from, and never counts as a sale.
    const known = {};
    real.forEach((r) => {
      if (r.sold != null) known[SHORT_KEY[r.label]] = r.sold;
    });
    const filled = estimateMissing(known);

    return real.map((r) => {
      const est = r.sold == null ? filled[SHORT_KEY[r.label]] : null;
      if (!est) return r;
      return {
        ...r,
        sold: est.price,
        estimated: true,
        estimateNote: describeEstimate(est, RAW_SHORT_LABELS),
      };
    }).filter((r) => r.sold != null || r.ask != null || r.mine);
  }, [askByCondition, grade, isGraded, evidence, heldOut]);

  const gradedByCompany = useMemo(() => {
    const out = {};
    for (const [tier, data] of Object.entries(tiers?.graded || {})) {
      if (price(data) == null) continue;
      const { company, grade: g } = formatTierLabel(tier);
      if (!company) continue;
      (out[company] = out[company] || []).push({ tier, grade: g, data });
    }
    for (const list of Object.values(out)) {
      list.sort((a, b) => parseFloat(b.grade) - parseFloat(a.grade));
    }
    return out;
  }, [tiers]);

  const companies = Object.keys(gradedByCompany).sort();
  // Primary graders first in their canonical order, everything else behind
  // "More". PSA is most of the market and should not sit third alphabetically.
  const primary = PRIMARY_GRADERS.filter((g) => companies.includes(g));
  const secondary = companies.filter((g) => !PRIMARY_GRADERS.includes(g));
  const visibleGraders = moreGraders ? [...primary, ...secondary] : primary;

  const myCompany = isGraded ? String(grade || '').trim().split(/\s+/)[0].toUpperCase() : null;
  const myGrade = isGraded ? String(grade || '').trim().split(/\s+/).slice(1).join('.') : null;
  const [company, setCompany] = useState(
    myCompany && companies.includes(myCompany) ? myCompany : primary[0] || companies[0] || null,
  );

  const activeCompany = company && gradedByCompany[company]
    ? company
    : (primary[0] || companies[0]);

  // Memoised, and it matters. The effect below reports the read upward, which
  // sets state in the parent and re-renders this component. A fresh array
  // identity every render would make the effect fire on its own output.
  // The rule every row obeys: a number you can see is a number you can open.
  //
  // The row used to read `own.value ?? price(r.data)`, which put the vendor's
  // aggregate on screen while `fromSales` stayed false - so the chip rendered
  // and the tap did nothing. That is how a Fossil Dragonite showed a PSA 10 at
  // $13,600 that refused to open, with not one PSA 10 sale held for it.
  //
  // Three states now, and no fourth. Sales back it, so it shows and it opens.
  // Nothing backs it, so it says so. Something backs it but barely, so it opens
  // and says to check it first.
  const gradedRows = useMemo(() => (gradedByCompany[activeCompany] || []).map((r) => {
    const own = headlinePrice(evidence, r.tier, heldOut);
    return {
      key: r.tier,
      label: `${activeCompany} ${r.grade}`,
      sold: own.value,
      fromSales: own.value != null,
      soldCount: own.value != null ? own.counted : null,
      thin: own.value != null && own.counted < DEFAULT_PREFS.count,
      ask: null,
      mine: isGraded && activeCompany === myCompany && r.grade === myGrade,
    };
  }), [gradedByCompany, activeCompany, isGraded, myCompany, myGrade, evidence, heldOut]);

  const rows = tab === 'raw' ? rawRows : gradedRows;

  // TCGplayer asking prices are a raw-only column, and fetching them costs a
  // full vendor lookup. A graded card never shows them, so they are asked for
  // the first time this tab is actually looked at rather than on arrival.
  useEffect(() => {
    if (tab === 'raw' && onNeedRawSources) onNeedRawSources();
  }, [tab, onNeedRawSources]);

  const [selected, setSelected] = useState(null);
  const activeKey = selected && rows.some((r) => r.key === selected)
    ? selected
    : (rows.find((r) => r.mine)?.key || rows[0]?.key || null);

  // Report the selected row so the chart above can draw the same tier. The
  // last value is compared before emitting: this sets state in the parent,
  // which re-renders this component, and an unstable reference upstream would
  // otherwise let the effect fire on its own output.
  const lastSentRef = useRef(null);
  useEffect(() => {
    if (!onSelectTier) return;
    const row = rows.find((r) => r.key === activeKey);
    const value = row ? { key: row.key, label: row.label } : null;
    const stamp = JSON.stringify(value);
    if (stamp === lastSentRef.current) return;
    lastSentRef.current = stamp;
    onSelectTier(value);
  }, [activeKey, rows, onSelectTier]);

  if (!rawRows.length && !companies.length) return null;

  const searchUrl = (label) => ebaySoldUrl({
    name: card?.name,
    setName: card?.set_name,
    number: card?.set_number,
    variant: card?.variant,
    grade: label,
  });

  const openDive = (key, src) => {
    haptics.tap();
    setSelected(key);
    onOpenBreakdown?.(key, src);
  };

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <span style={s.title}>Pricing</span>
        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(tab === 'raw' ? s.tabOn : {}) }}
            onClick={() => { haptics.select(); setTab('raw'); setSelected(null); }}
          >Raw</button>
          <button
            style={{ ...s.tab, ...(tab === 'graded' ? s.tabOn : {}) }}
            onClick={() => { haptics.select(); setTab('graded'); setSelected(null); }}
          >Graded</button>
        </div>
      </div>

      {tab === 'graded' && !companies.length ? (
        <div style={s.emptyTab}>
          No graded sales on record for this card.
          {' '}
          A slab has to have sold before we can price one.
        </div>
      ) : tab === 'graded' ? (
        <>
          {companies.length > 0 && (
            <div style={s.chips}>
              {visibleGraders.map((g) => (
                <button
                  key={g}
                  style={{ ...s.chip, ...(g === activeCompany ? s.chipOn : {}) }}
                  onClick={() => { haptics.select(); setCompany(g); setSelected(null); }}
                >{g}</button>
              ))}
              {secondary.length > 0 && !moreGraders && (
                <button
                  style={s.chip}
                  onClick={() => { haptics.tap(); setMoreGraders(true); }}
                >More</button>
              )}
            </div>
          )}

          {/* States the rule the numbers below were produced by. Three
              different figures for one card appeared on this screen because
              nothing said which method any of them used; naming it once removes
              the guess. */}
          <div style={s.method}>Average of the last 3 sold, questionable held out</div>

          <div style={s.card}>
            {gradedRows.map((r, i) => (
              <div key={r.key} style={{ ...s.gradeRow, ...(i ? s.divided : {}) }}>
                {/* Leaves the app. Link blue with the external icon, which is
                    the one visual language used for outbound anywhere here. */}
                <a
                  style={s.gradeLink}
                  href={searchUrl(r.label)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => haptics.tap()}
                >
                  {r.label}
                  <ExternalLink size={12} />
                </a>
                {r.soldCount != null && !soldPending && (
                  <span style={{ ...s.soldCount, ...(r.thin ? s.soldCountThin : {}) }}>
                    {r.soldCount} sold{r.thin ? ' · check it' : ''}
                  </span>
                )}
                {soldPending ? (
                  <span style={s.pending} />
                ) : r.sold == null ? (
                  <span style={s.na}>{COPY.noSales}</span>
                ) : (
                  <button
                    style={{
                      ...s.priceChip,
                      ...(r.fromSales ? {} : s.priceChipFlat),
                      ...(r.thin ? s.priceChipThin : {}),
                    }}
                    onClick={() => r.fromSales && openDive(r.key, 'eBay sold')}
                    disabled={!r.fromSales}
                  >
                    {money2(r.sold)}
                    {r.fromSales && <ChevronRight size={13} style={{ opacity: 0.7 }} />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={s.method}>Average of the last 3 sold, questionable held out</div>
          {rawRows.map((r) => (
          <div key={r.key} style={{ ...s.card, ...(r.mine ? s.cardMine : {}), marginBottom: '10px' }}>
            <div style={s.groupHead}>
              <span style={s.groupName}>{r.word}</span>
              {r.mine && <span style={s.yours}>YOURS</span>}
            </div>
            <div style={s.tiles}>
              <MarketTile
                s={s}
                c={c}
                label={r.estimated ? 'Estimate' : 'eBay sold'}
                value={r.sold}
                estimated={r.estimated}
                pending={soldPending}
                canDive={r.fromSales}
                onDive={() => openDive(r.key, 'eBay sold')}
                href={searchUrl(r.label)}
              />
              <MarketTile
                s={s}
                c={c}
                label="TCGplayer"
                value={r.ask}
                pending={asksPending}
                canDive={false}
                href={tcgplayerUrl({
                  productId: tiers?.tcgplayer_product_id || null,
                  name: card?.name,
                  setName: card?.set_name,
                  number: card?.set_number,
                })}
              />
            </div>

            {/* Says where the number came from, in words. An estimate that does
                not explain itself is just a price somebody made up. */}
            {r.estimateNote && <div style={s.estimateNote}>{r.estimateNote}</div>}
          </div>
        ))}
        </>
      )}

      {tiers?.tieBroken && (
        <div style={s.pricedAs}>
          Priced as <strong style={s.pricedAsName}>{tiers.tieBroken.chosen}</strong>
          {tiers.tieBroken.variant ? `, ${tiers.tieBroken.variant}` : ''}.
          {' '}This number matches the standard printing, not{' '}
          {tiers.tieBroken.rejected.join(' or ')}.
        </div>
      )}

      <div style={s.provided}>
        {tab === 'raw' ? COPY.attributionRaw : COPY.attributionGraded}
      </div>
    </div>
  );
}

/**
 * One market inside a quality group. Two targets in one shape.
 *
 * The body opens the deep dive. The carved corner leaves for the marketplace.
 * The corner is a real button with stopPropagation, and its touch target is
 * padded past what it draws, because a 36px icon in the corner of a tappable
 * tile is otherwise a coin toss.
 */
function MarketTile({ s, c, label, value, pending, canDive, onDive, href, estimated }) {
  const body = (
    <>
      <span style={{ ...s.tileLabel, ...(estimated ? { color: c.holo } : {}) }}>{label}</span>
      {pending ? (
        <span style={s.pending} />
      ) : value == null ? (
        <span style={s.tileEmpty}>{COPY.noSales}</span>
      ) : (
        <span style={{ ...s.tileValue, ...(estimated ? s.tileValueEstimate : {}) }}>
          {/* A tilde, because it is about this much and not this much. */}
          {estimated ? `~${money2(value)}` : money2(value)}
          {/* Chevron only where there is somewhere to go. */}
          {canDive && <ChevronRight size={14} style={{ opacity: 0.6, flex: 'none' }} />}
        </span>
      )}
    </>
  );

  return (
    <div style={s.tile}>
      {canDive ? (
        <button style={s.tileBody} onClick={onDive}>{body}</button>
      ) : (
        <div style={{ ...s.tileBody, cursor: 'default' }}>{body}</div>
      )}
      <a
        style={s.tileCorner}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { e.stopPropagation(); haptics.tap(); }}
        aria-label={`Search ${label}`}
      >
        <ExternalLink size={13} />
      </a>
    </div>
  );
}

const styles = (c) => ({
  wrap: { marginTop: '4px' },

  head: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '12px',
  },
  title: {
    fontSize: '12px', fontWeight: '600', letterSpacing: '0.11em',
    textTransform: 'uppercase', color: c.muted,
  },
  tabs: {
    display: 'flex', background: c.input, border: `0.5px solid ${c.hairline}`,
    borderRadius: '999px', padding: '3px',
  },
  tab: {
    border: 0, background: 'transparent', color: c.secondary, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: '13px', fontWeight: '500', lineHeight: 1,
    padding: '8px 16px', borderRadius: '999px',
  },
  tabOn: { background: '#2c2f35', color: c.white },

  chips: { display: 'flex', gap: '7px', flexWrap: 'wrap', margin: '0 0 10px' },
  method: {
    fontSize: '10.5px', color: c.faint, letterSpacing: '0.02em', margin: '0 0 9px 2px',
  },
  emptyTab: {
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '14px',
    padding: '18px 16px', fontSize: '13px', color: c.secondary, lineHeight: 1.5,
  },
  chip: {
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.secondary,
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '500', lineHeight: 1,
    padding: '8px 14px', borderRadius: '999px', cursor: 'pointer',
  },
  chipOn: { border: `0.5px solid ${c.blue}`, background: c.blueTint, color: c.blue },

  card: {
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '14px',
    padding: '4px 14px',
  },
  cardMine: { background: c.lifted, border: `0.5px solid ${c.liftedBorder}` },
  divided: { borderTop: `0.5px solid ${c.divider}` },

  // ---- graded ----
  gradeRow: { display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 0' },
  gradeLink: {
    display: 'inline-flex', alignItems: 'center', gap: '5px', flex: 'none',
    color: c.link, textDecoration: 'none', fontSize: '16px', fontWeight: '500',
  },
  soldCount: { fontSize: '12px', color: c.muted, whiteSpace: 'nowrap' },
  priceChip: {
    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px',
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.white,
    fontFamily: 'inherit', fontSize: '16px', fontWeight: '500', lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    padding: '10px 11px', borderRadius: '8px', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  priceChipFlat: { border: '0.5px solid transparent', cursor: 'default', paddingRight: '2px' },
  // Priced off fewer sales than the pricing rule asks for. The number is real
  // and it opens, and it is one or two sales from meaning nothing, so it does
  // not sit at the same weight as a tier with thirty behind it. Same amber the
  // estimate rows use, because it is the same claim: treat this one carefully.
  //
  // `border` and not `borderColor` - priceChip sets the shorthand, and mixing
  // the two drops the rule.
  priceChipThin: { border: `0.5px solid ${c.holo}` },
  soldCountThin: { color: c.holo },

  // ---- raw ----
  groupHead: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0 2px' },
  groupName: { fontSize: '16px', fontWeight: '500', color: c.white },
  yours: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.09em',
    color: c.blue, background: c.blueTint, padding: '3px 6px', borderRadius: '4px',
  },
  tiles: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '8px 0 12px',
  },
  tile: {
    position: 'relative', background: c.tile, border: `0.5px solid ${c.chipBorder}`,
    borderRadius: '10px', overflow: 'hidden',
  },
  tileBody: {
    display: 'flex', flexDirection: 'column', gap: '5px', width: '100%',
    border: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer',
    padding: '11px 12px 12px', color: 'inherit', fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
  },
  tileLabel: { fontSize: '11px', color: c.muted },
  tileValue: {
    display: 'flex', alignItems: 'center', gap: '4px',
    fontSize: '18px', fontWeight: '500', color: c.white,
    fontVariantNumeric: 'tabular-nums',
  },
  tileEmpty: { fontSize: '13px', color: c.muted },
  tileValueEstimate: { color: c.holo, fontWeight: '400' },
  estimateNote: {
    fontSize: '10.5px', color: c.faint, lineHeight: 1.45, padding: '0 2px 10px',
  },
  tileCorner: {
    position: 'absolute', top: 0, right: 0,
    width: '36px', height: '34px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: c.card, borderLeft: `0.5px solid ${c.chipBorder}`,
    borderBottom: `0.5px solid ${c.chipBorder}`, borderBottomLeftRadius: '10px',
    color: c.link, textDecoration: 'none',
    // Draws at 36x34, catches at 44x44, without moving the seam.
    boxSizing: 'content-box', padding: '5px', margin: '-5px -5px 0 0',
    WebkitTapHighlightColor: 'transparent',
  },

  na: { marginLeft: 'auto', fontSize: '13px', color: c.muted },
  pending: {
    display: 'inline-block', width: '54px', height: '13px', borderRadius: '5px',
    background: c.chipBorder, opacity: 0.8,
    animation: 'cc-pulse 1.4s ease-in-out infinite',
  },

  pricedAs: {
    fontSize: '12px', color: c.secondary, marginTop: '12px', lineHeight: 1.5,
    padding: '9px 11px', borderRadius: '10px',
    background: c.lifted, border: `0.5px solid ${c.hairline}`,
  },
  pricedAsName: { color: c.white, fontWeight: '600' },
  provided: {
    fontSize: '11px', color: c.faint, marginTop: '14px', lineHeight: 1.5,
    textAlign: 'center',
  },
});
